import { useEffect, useMemo, useState } from 'react';
import { CompanyMenu } from './components';
import { Box, Button, Drawer, List, ListItem, ListItemIcon, ListItemText, Typography } from '@material-ui/core';
import { map } from 'lodash';
import { SidebarWidth } from 'themes/main';
import { useAuthFunctions } from 'contexts/AuthContext';
import { useHistory, useLocation } from 'react-router-dom';
import { useProvider } from 'contexts/ProviderContext';
import { useTheme } from '@material-ui/core/styles';
import activitiesLogo from '_assets/dashboard/activities-logo.svg';
import activitiesLogoBold from '_assets/dashboard/activities-logo-bold.svg';
import agendaLogo from '_assets/dashboard/agenda-logo.svg';
import agendaLogoBold from '_assets/dashboard/agenda-logo-bold.svg';
import clientsLogo from '_assets/dashboard/clients-logo.svg';
import clientsLogoBold from '_assets/dashboard/clients-logo-bold.svg';
import dashboardLogo from '_assets/dashboard/dashboard-logo.svg';
import dashboardLogoBold from '_assets/dashboard/dashboard-logo-bold.svg';
import envelopeLogo from '_assets/dashboard/envelope.svg';
import envelopeLogoBold from '_assets/dashboard/envelope-bold.svg';
import helpLogo from '_assets/dashboard/help-logo.svg';
import helpLogoBold from '_assets/dashboard/help-logo-bold.svg';
import instagramLogo from '_assets/common/instagram-logo.svg';
import instagramLogoBold from '_assets/common/instagram-logo-bold.svg';
import MaiaLogo from 'components/MaiaLogo';
import ProviderProfilePicture from 'components/ProviderProfilePicture';
import routes from 'config/routes';
import settingsLogo from '_assets/dashboard/settings-logo.svg';
import settingsLogoBold from '_assets/dashboard/settings-logo-bold.svg';
import useStyles from './styles';

const firstItems = [
  { id: 0, route: routes.home.root, icon: dashboardLogo, boldIcon: dashboardLogoBold, text: 'Tableau de bord' },
  { id: 1, route: routes.activities.root, icon: activitiesLogo, boldIcon: activitiesLogoBold, text: 'Activités' },
  { id: 2, route: routes.clients.root, icon: clientsLogo, boldIcon: clientsLogoBold, text: 'Clients' },
  { id: 3, route: routes.agenda.root, icon: agendaLogo, boldIcon: agendaLogoBold, text: 'Agenda' },
  { id: 4, route: routes.messaging.root, icon: envelopeLogo, boldIcon: envelopeLogoBold, text: 'Messagerie' },
  {
    id: 5,
    route: routes.socialNetworks.root,
    icon: instagramLogo,
    boldIcon: instagramLogoBold,
    text: 'Réseaux Sociaux',
  },
];

const secondItems = [
  { id: 6, route: routes.help.root, icon: helpLogo, boldIcon: helpLogoBold, text: 'Assistance' },
  { id: 7, route: routes.parameters.root, icon: settingsLogo, boldIcon: settingsLogoBold, text: 'Paramètres' },
];

const SideBar = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();

  const { logout } = useAuthFunctions();
  const { provider } = useProvider();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const status = useMemo(() => (provider && provider.is_confirmed ? 'Confirmé' : 'En attente'), [provider]);

  useEffect(() => {
    switch (location.pathname.split('/')[1]) {
      case 'activities':
        setSelectedIndex(1);
        break;
      case 'clients':
        setSelectedIndex(2);
        break;
      case 'agenda':
        setSelectedIndex(3);
        break;
      case 'messaging':
        setSelectedIndex(4);
        break;
      case 'social-networks':
        setSelectedIndex(5);
        break;
      case 'help':
        setSelectedIndex(6);
        break;
      case 'parameters':
        setSelectedIndex(7);
        break;
      default:
        setSelectedIndex(0);
        break;
    }
  }, [location]);

  const handleDisconnect = async () => {
    await logout();
    await history.push(routes.home.root);
  };

  const SidebarContent = (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <ProviderProfilePicture />
        <Box className={classes.statusContainer}>
          <div
            className={`${classes.statusEllipse} ${
              classes[status === 'Confirmé' ? 'confirmedStatus' : 'pendingStatus']
            }`}
          />
          <Typography className={classes.statusText}>{status}</Typography>
        </Box>
        <Typography className={classes.welcomeText} variant="subtitle1">
          Bonjour {provider?.first_name} !
        </Typography>
        <CompanyMenu />
      </Box>

      {[firstItems, secondItems].map((items, index) => (
        <Box key={`box-${index}`} className={classes.listContainer}>
          <List>
            {map(items, ({ id, route, icon, boldIcon, text }) => (
              <ListItem
                button
                className={classes.listItem}
                key={id}
                onClick={() => {
                  history.push(route);
                }}
                selected={selectedIndex === id}
                style={{
                  ...(selectedIndex === id
                    ? {
                        color: 'white',
                        backgroundColor: theme.palette.primary.main,
                        border: `1px solid ${theme.palette.blue.light}`,
                      }
                    : {
                        color: theme.palette.primary.main,
                      }),
                }}
              >
                <ListItemIcon>
                  <i
                    style={{
                      WebkitMaskSize: 'cover',
                      maskSize: 'cover',
                      WebkitMaskImage: selectedIndex === id ? `url(${boldIcon})` : `url(${icon})`,
                      maskImage: `url(${icon})`,
                      backgroundColor: selectedIndex === id ? 'white' : theme.palette.common.black,
                      width: 24,
                      height: 24,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ style: selectedIndex === id ? { fontWeight: 500 } : { fontWeight: 300 } }}
                >
                  {text}
                </ListItemText>
              </ListItem>
            ))}
          </List>
        </Box>
      ))}

      <Box className={classes.logoutContainer}>
        <Button className={classes.logoutButton} onClick={handleDisconnect}>
          Se déconnecter
        </Button>
        <MaiaLogo />
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor="left"
      open={true}
      variant="persistent"
      PaperProps={{
        style: {
          backgroundColor: theme.palette.blue.lightest,
          width: SidebarWidth,
        },
      }}
    >
      {SidebarContent}
    </Drawer>
  );
};

export default SideBar;
